import {
    BlurEvent, ClickEvent, DataSourceAction, DataSourceExecutionEvent, DataSourceMode, DataSourceModeChangeEvent,
    Layout, LayoutProvidedCrudDecoratorProps, Location, LookupModelSearchEvent, LookupModelSelectionEvent, Snackbar,
    StepEvent, Textbox,
} from "@mcleod/components";
import {
    Api, CurrencySettings, DateRange, DisplayType, DisplayValue, LogManager, ModelRow, StringUtil,
    UrlUtil
} from "@mcleod/core";
import { getDispatchControlBoolean, LMEGeneralSettings, LMEUserSettings } from "@mcleod/general";
import { RATE_VENDORS } from "@mcleod/dispatch";
import { AutogenLayoutQuickQuote } from "./autogen/AutogenLayoutQuickQuote";
import { ModelProspect } from "@mcleod/crm/src/models/ModelProspect";

enum QuoteStep {
    QUOTE_ENTRY = 0,
    SUGGESTED_PRICING = 1,
    NEXT_ACTIONS = 2
}

const log = LogManager.getLogger("lme.powerbroker.QuickQuote");

export class QuickQuote extends AutogenLayoutQuickQuote {

    override onLoad() {
        this.configureSaveButton();
        this.setCustomerNameListeners();
        this.setupQuoteStopTable();
        if (CurrencySettings.getSingleton().multiCurrencyEnabled()) {
            this.layoutQuoteRates.textboxCurrencyType.defaultDataValue = CurrencySettings.getTransactionalCurrency();
            this.layoutQuoteRates.convertPricingCurrencies(CurrencySettings.getTransactionalCurrency());
            this.layoutQuoteRates.textboxCurrencyType.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => {
                this.layoutQuoteRates.textboxCurrencyTypeOnLookupModelSelection(event);
            });
        } else {
            this.layoutQuoteRates.textboxCurrencyType.defaultDataValue = CurrencySettings.getFunctionalCurrency();
        }
        const partialShipmentsEnabled = "Y" == LMEGeneralSettings.getSingleton().getDispatchControl()["enable_manifesting"];
        this.textboxOrderLength.visible = partialShipmentsEnabled;
    }

    async textboxOrderLengthOnBlur(event: BlurEvent) {
        const textboxOrderLength: Textbox = event.target as Textbox;
        const orderLength = textboxOrderLength.text;
        if (!StringUtil.isEmptyString(orderLength)) {
            const parsedValue = parseFloat(orderLength);
            if (isNaN(parsedValue) || parsedValue <= 0) {
                this.activeRow.set({ order_length: null });
                await Snackbar.showWarningSnackbar('Order length must be greater than zero.');
                textboxOrderLength.focus();
            }
        }
    }

    public override get crudDecoratorProps(): Partial<LayoutProvidedCrudDecoratorProps> {
        return { customDataLoader: (apiData: any, mode: DataSourceMode) => this.loadFromApiData(apiData, mode) };
    }

    public loadFromApiData = (apiData: any, mode: DataSourceMode) => {
        this.mainDataSource.createBlankRow().then(row => {
            row.setValues(apiData.orderData);
            this.mainDataSource.setRowsAndMode(mode, [row]);
            const stopData: ModelRow[] = apiData.stopData;
            const quoteStops = stopData?.map(stop => new ModelRow(this.layoutQuoteStopTable.sourceQuoteStop.url, true, stop.data))
            this.layoutQuoteStopTable.sourceQuoteStop.setRowsAndMode(mode, quoteStops);
        })
    }

    setCustomerNameListeners() {
        this.textboxCustomerName.addLookupModelSelectionListener(this.customerNameSelectionListener);
        this.textboxCustomerName.addDataDisplayListener(event => this.setCustomerNameTooltip());
        this.textboxCustomerName.addChangeListener(event => {
            if (StringUtil.isEmptyString(event.newValue)) {
                this.activeRow.set("customer_name", null);
                this.activeRow.set("customer_id", null);
                this.clearCustomerContactInfo();
            }
        });
        this.textboxCustomerContactName.addLookupModelSelectionListener(this.textboxContactNameOnLookupModelSelection);
    }

    public customerNameSelectionListener = (event: LookupModelSelectionEvent) => {
        this.handleCustomerNameSelection(event.selection);
    };

    public handleCustomerNameSelection = (selectedRow: ModelRow) => {
        if (selectedRow != null) {
            this?.activeRow.set("customer_id", selectedRow.get("id"));
            this?.activeRow.set("salesperson_id", selectedRow.get("salesperson_id"));
            this.clearCustomerContactInfo();
            this.setCustomerNameTooltip();
            if (CurrencySettings.getSingleton().multiCurrencyEnabled()) {
                this.layoutQuoteRates.convertPricingCurrencies(selectedRow.get("currency_type", CurrencySettings.getTransactionalCurrency()));
            }
            this.layoutQuoteStopTable.tableStop.rows?.forEach(tableRow => {
                const location = tableRow["layoutQuoteStop"]?.locationStop as Location;
                if (location) {
                    location.googlePlacesProps["customerId"] = this.mainDataSource.activeRow?.get("customer_id");
                }
            });
        }
    };

    public textboxContactNameOnLookupModelSelection = (event: LookupModelSelectionEvent) => {
        this.handleContactNameSelection(event.selection);
    };

    public handleContactNameSelection = (selectedRow: ModelRow) => {
        if (selectedRow != null) {
            this.activeRow.set("customer_phone", selectedRow.get("phone"));
            this.activeRow.set("customer_email", selectedRow.get("email"));
        }
    };

    private clearCustomerContactInfo() {
        this.activeRow.set("customer_contact_name", null);
        this.activeRow.set("customer_phone", null);
        this.activeRow.set("customer_email", null);
        this.textboxCustomerContactName.clear();
    }

    private async setupQuoteLanding() {
        // There is an issue when simulatenously loading QuoteRates in Step 2 and Step 3. (Invalid parentDataSource)
        // This is a workaroud, where we manually set the datasources for QuoteLanding for QuickQuote process
        this.layoutQuoteLanding.panelNextActionsAdd.visible = true;
        await this.layoutQuoteLanding.mainDataSource.search({"id": this.activeRow.get("id")});
        this.buttonSave.dataSource = this.layoutQuoteLanding.mainDataSource;
        this.layoutQuoteLanding.saveButton = this.buttonSave;
        if (this.buttonSave.busy) {
            this.buttonSave.busy = false;
        }
    }

    private setupQuoteStopTable() {
        this.layoutQuoteStopTable.orderSource = this.mainDataSource;
    }

    setCustomerNameTooltip() {
        const lookupModelData = this.textboxCustomerName.getFirstLookupModelData();
        this.textboxCustomerName.quickInfoOverrideKeyValue = lookupModelData?.get("id");
    }

    textboxCustomerNameBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.is_active = "!N";
    }

    configureSaveButton(index?: number) {
        const lastStep = this.stepperQuickQuote.stepCount == (index ?? this.stepperQuickQuote.selectedIndex) + 1;
        this.buttonSave.visible = index == QuoteStep.SUGGESTED_PRICING || lastStep;
        this.buttonSave.caption = index == QuoteStep.SUGGESTED_PRICING ? "Generate Quote" : "Submit";
        this.buttonSave.width = index == QuoteStep.SUGGESTED_PRICING ? 150 : 125;
        this.buttonContinue.visible = index !== QuoteStep.SUGGESTED_PRICING && !lastStep;
    }

    checkStopsForDistanceChange(): boolean {
        const stopdata = this.sourceQuoteStop.data;
        if (this.sourceQuoteStop.deletedData?.length > 0) return true;
        for (let i = 0; i < stopdata?.length; i++) {
            if (stopdata[i]._appending || stopdata[i].getChangedData()?.city_id != null) return true;
        }
        return false;
    }

    buttonContinueOnClick(event: ClickEvent) {
        const stepper = this.stepperQuickQuote;
        stepper.selectedIndex = stepper.selectedIndex + 1;
    }

    textboxContactNameBeforeLookupModelSearch(event) {
        if (this.activeRow.isNull("customer_id")) {
            event.preventDefault();
        } else {
            event.filter.parent_row_id = this.activeRow.get("customer_id");
            event.filter.parent_row_type = "C";
            event.filter.is_active = "Y";
        }
    }

    populateAdditionalPricingInformation() {
        this.populateLaneHistoryInformation();
        this.populateRateIndexInformation();
    }

    async populateRateIndexInformation(): Promise<void> {
        const rateRetrievalList: string[] = [];
        const data = await this.sourceRateIndexControl.search();
        const modelRows = data?.modelRows ?? [];
        const firstSetupVendor = modelRows.find(item => item.data?.licensed_and_setup);
        this.tabRateIndex.visible = !!firstSetupVendor;
         if(this.tabRateIndex.visible) {
        modelRows.forEach((row) => {
            const autoRateRetrieval = row?.data?.auto_rate_retrieval;
            const isLicensedAndSetup = row?.data?.licensed_and_setup;
            if (isLicensedAndSetup && autoRateRetrieval?.toUpperCase() === "Y") {
                rateRetrievalList.push(row?.data?.vendor);
            }
        });

        this.layoutRateIndex.setDefaultValues();
        this.layoutRateIndex.hideColumns();
        this.layoutRateIndex.buttonFetchOtherResults.visible = true;

        const stopData = this.sourceQuoteStop.data.map((value) => value.data);
        this.layoutRateIndex.marginBottom = 20;
        const isRateRetrievalListPopulated = rateRetrievalList.length > 0;
        const rateIndexSearchFilter = {
            stops: stopData,
            equipment_id: this.activeRow.get("equipment_type_id"),
            show_all_total_rates: true,
            run_type: 3,
            mileage: this.activeRow.get("bill_distance"),
            vendor: RATE_VENDORS.MPACT
        };

        if (isRateRetrievalListPopulated) {
            await Promise.all(
                rateRetrievalList.map((vendor) =>
                    this.fetchSuggestedPricingRates(stopData, this.activeRow.get("bill_distance"), this.activeRow.get("equipment_type_id"), vendor)
                )
            );
            this.layoutRateIndex.search(rateIndexSearchFilter);
        }
        if (!isRateRetrievalListPopulated) {
            this.layoutRateIndex.mainDataSource.lastSearch = rateIndexSearchFilter;
        }
    }
    }


    async fetchSuggestedPricingRates(stops: any[], mileage: number, equipmentId: string, vendor: string): Promise<void> {
        try {
            const response = await Api.search("lme/dispatch/rate-index-results-data", {
          //  const response = this.layoutRateIndex.search({
                stops: stops,
                equipment_id: equipmentId,
                show_all_total_rates: true,
                run_type: 3,
                mileage: mileage,
                vendor: vendor,
            });

           if (response?.data?.[0]?.vendors_data) {
                const data = response.data[0];
                for (const vendorData of data.vendors_data) {
                    this.layoutRateIndex.populateRateIndexResultData(vendorData, true, data.show_company_scac, null);
                }
            } else {
                log.error(`Invalid response structure for vendor: ${vendor}`);
            }
        } catch (error) {
            log.error(`Error fetching suggested pricing rates for vendor: ${vendor}`, error);
        }
    }


    populateLaneHistoryInformation() {
        const findNearCityId = this.sourceQuoteStop.data[0].get("city_id");
        const destinationState = this.sourceQuoteStop.data[this.sourceQuoteStop.data.length - 1].get("state");
        this.textboxRadius.text = "75";
        const filter: ModelRow = this.layoutLaneHistory.sourceLaneHistory.getSearchFilter();
        filter.setValues({
            find_near_city_id: findNearCityId,
            destination_state: destinationState
        });
        this.layoutLaneHistory.search(filter);
    }

    setStopData() {
        const shipperStop = this.sourceQuoteStop.data[0];
        const consigneeStop = this.sourceQuoteStop.data[this.sourceQuoteStop.data.length - 1];
        this.mainDataSource.activeRow.setValues({
            "stop_origin.location_name": shipperStop.get("location_name"),
            "stop_origin.city_name": shipperStop.get("city_name"),
            "stop_origin.state": shipperStop.get("state"),
            "stop_origin.zip_code": shipperStop.get("zip_code"),
            "stop_consignee.location_name": consigneeStop.get("location_name"),
            "stop_consignee.city_name": consigneeStop.get("city_name"),
            "stop_consignee.state": consigneeStop.get("state"),
            "stop_consignee.zip_code": consigneeStop.get("zip_code")
        })
        this.citystateShipperCS.quickInfoLayout = null; // Defeat normal city/state quick info
        this.citystateShipperCS.tooltipDynamicContent = async () => await this.getLocationQuickInfoTooltipContent(shipperStop.get("location_id"));
        this.citystateConsigneeCS.quickInfoLayout = null; // Defeat normal city/state quick info
        this.citystateConsigneeCS.tooltipDynamicContent = async () => await this.getLocationQuickInfoTooltipContent(consigneeStop.get("location_id"));
        this.citystateStopOriginCityId.displayComponentData(shipperStop, null, 0);
        this.mainDataSource.displayDataInBoundComponents();
    }

    private async getLocationQuickInfoTooltipContent(keyValue: string): Promise<Layout> {
        if (StringUtil.isEmptyString(keyValue) === true) {
            return null;
        }
        const layout = await Layout.getLoadedLayout("lme/dispatch/LocationQuickInfo");
        await layout.mainDataSource?.search({ search: keyValue });
        return layout;
    }

    async stepperQuickQuoteBeforeStepListener(event: StepEvent) {
        this.mainDataSource.displayDataInBoundComponents();
        switch (event.oldIndex) {
            case QuoteStep.QUOTE_ENTRY:
                if (!this.layoutQuoteStopTable.validateStops()) {
                    event.preventDefault();
                    return;
                }
                this.setStopData();
                break;
            case QuoteStep.NEXT_ACTIONS:
                event.preventDefault();
                return;
        }

        switch (event.newIndex) {
            case QuoteStep.SUGGESTED_PRICING:
                if (event.oldIndex == QuoteStep.QUOTE_ENTRY) {
                    if (this.checkStopsForDistanceChange()) {
                        const rateId = this.activeRow.get("rate_id");
                        this.activeRow.set("bill_distance", null);
                        await this.layoutQuoteRates.calculateRates(false, getDispatchControlBoolean("est_fuel_surcharge"), rateId);
                        if (this.layoutQuoteRates.buttonExpand.imageRotation != 90) {
                            this.layoutQuoteRates.buttonExpand.clicked(); // toggle collapse of additional charges as default view
                        }
                        this.layoutQuoteRates.textboxRateType.focus();
                        this.populateAdditionalPricingInformation();
                    }
                }
                break;
            case QuoteStep.NEXT_ACTIONS:
                if (this.activeRow.isNull("id")) {
                    event.preventDefault();
                    return;
                }
                break;
        }
        this.configureSaveButton(event.newIndex);
    }

    // END - Component Events

    sourceLaneHistoryBeforeExecution(event: DataSourceExecutionEvent) {
        if (DataSourceAction.SEARCH === event.getAction()) {
            event.preventDefault();
            this.searchLaneHistoryFilter();
        }
    }

    private searchLaneHistoryFilter() {
        const stopdata = this.sourceQuoteStop.data;
        const findNearCityId = stopdata[0].get("city_id");
        const destinationState = stopdata[stopdata.length - 1].get("state");
        const filter: ModelRow = this.layoutLaneHistory.sourceLaneHistory.getSearchFilter();
        if (filter.isNull("date_range")) {
            const dateRange = DateRange.parseDateRangeWithKeywords("t-180,t");
            this.textboxDateRange.text = DisplayValue.getDisplayValue(dateRange, DisplayType.DATERANGE, this.textboxDateRange.format);
            filter.setValues({
                date_range: dateRange,
                "date_range.start": dateRange.startDate,
                "date_range.end": dateRange.endDate
            });
        }
        if (filter.isNull("find_near_radius")) {
            this.textboxRadius.text = "75";
            filter.set("find_near_radius", 75)
        }
        filter.setValues({
            find_near_city_id: findNearCityId,
            destination_state: destinationState
        });
        this.layoutLaneHistory.search(filter);
    }

    // END - Expandable Filter Logic

    // START - Commodity Code
    /** This is an event handler for the onChange event of textboxCommodityId.  */
    textboxCommodityIdOnChange(event) {
        if (event.userInitiatedChange) {
            this.updateCommodityCode();
        }
    }

    private updateCommodityCode() {
        const commodityCleared = this.activeRow.isNull("commodity_id");
        const lookupData = commodityCleared ? null : this.textboxCommodityId.getFirstLookupModelData();
        this.activeRow.set("commodity", lookupData?.get("descr"));
        this.showHazmatWarning();
    }

    private showHazmatWarning() {
        const lookupData = this.textboxCommodityId.getFirstLookupModelData();
        const hazmat = lookupData?.get("is_hazmat");
        this.panelQuoteOrderFields.marginTop = hazmat === "Y" ? 35 : 0;
        this.panelCommodityHazmat.padding = hazmat === "Y" ? 9 : 0;
        this.panelCommodityHazmat.borderWidth = hazmat === "Y" ? 1 : 0;
        this.panelCommodityHazmat.borderColor = hazmat === "Y" ? "error" : "";
        this.labelHazmatCommodity.visible = this.imageHazmat.visible = hazmat === "Y";
    }

    // END - Commodity Code

    // START - Datasource Events
    async sourceQuoteStopAfterModeChange(event: DataSourceModeChangeEvent) {
        this.textboxWeight.addBlurListener(event => {
            if (event.changedWhileFocused) this.layoutQuoteRates.calculateRates(false, false)
        });
    }

    async sourceQuoteOrderAfterModeChange(event: DataSourceModeChangeEvent) {
        const urlParams = UrlUtil.getPropsFromUrl(window.location.href);
        const customerId = urlParams?.customerId;
        if (customerId) {
            const customerData = await new ModelProspect().searchSingle({ id: customerId });
            if (customerData) {
                this.sourceQuoteOrder.activeRow.setValueAndLookupModelData("customer_name", customerData.getString("name"), customerData);
                this.handleCustomerNameSelection(customerData);
            }

            const contact = urlParams?.contact;
            if (contact) {
                const phone = urlParams?.phone;
                const email = urlParams?.email;
                const contactLookupData = new ModelRow(null, false, { name: contact, phone, email });
                this.sourceQuoteOrder.activeRow.setValueAndLookupModelData("customer_contact_name", contact, contactLookupData);
                this.handleContactNameSelection(contactLookupData);
            }
        }
    }
    sourceQuoteOrderAfterExecution(event: DataSourceExecutionEvent) {
        if (!this.activeRow.isNull("id")) {
            this.buttonSave.allowSaveAndClose = this.dataheaderQuote.showClose;
            this.buttonSave.allowSave = !this.dataHeader.showClose;
            this.stepperQuickQuote.selectedIndex = QuoteStep.NEXT_ACTIONS;
            this.setupQuoteLanding();
            if (DataSourceAction.ADD === event.getAction() && event.isAfter()) {
                const userSettings = LMEUserSettings.get();
                if (userSettings?.quote_pend_act_create || userSettings?.quote_comp_act_create) {
                    Api.post("lme/crm/create-quote-activity", {id: this.activeRow.get("id")})
                    .then(result => {
                        if (result?.data?.[0]?.message){
                            Snackbar.showWarningSnackbar(result.data[0].message, {warningTitle: "Could Not Create Activity Records"});
                        }
                    });
                }
            }
        }
    }

    sourceQuoteOrderBeforeModeChange(event: DataSourceModeChangeEvent) {
        if (event.newMode !== DataSourceMode.ADD) {
            this.title = "Quote Number " + this.activeRow.get("id");
            this.hideSuffix = true;
        }
    }

    // END - Datasource Events

    private async fetchRateIndexResults(stopData: any[], billDistance: number, equipmentTypeId: string, vendor: string): Promise<void> {
        try {
            const response = await this.layoutRateIndex.search({
                stops: stopData,
                mileage: billDistance,
                equipment_id: equipmentTypeId,
                vendor: vendor,
                show_all_total_rates: true,
            });

            if (response && response.modelRows?.[0]?.data) {
                const data = response.modelRows[0].data;

                for (const vendorData of data.vendors_data || []) {
                    this.layoutRateIndex.populateRateIndexResultData(
                        vendorData,
                        true,
                        data.show_company_scac,
                        null
                    );
                }
            } else {
                log.error("No valid data received from rate index response for Quick Quote");
            }
        } catch (error) {
            log.error(
                "Error occurred while fetching rate index results for Quick Quote",
                error
            );
        }
    }
}
